/**
 * /services/international/apply &mdash; Self-service apply form for the
 * four intl service-pro personas (walker / sitter / groomer / trainer).
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react";

const KINDS = [
  { v: "walker", label: "Dog Walker" },
  { v: "sitter", label: "Sitter / Boarding" },
  { v: "groomer", label: "Groomer" },
  { v: "trainer", label: "Trainer" },
];

export default function InternationalServiceProApply() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    service_kind: search.get("kind") || "walker",
    display_name: "", legal_name: "", email: "", phone: "",
    home_country: "", cities_text: "",
    years_in_business: 0, certs_text: "",
    insurance_carrier: "", insurance_liability_usd: 0,
    insurance_certificate_url: "", website_url: "",
    bio: "", photo_url: "",
    accepted_agreement: false,
  });
  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.accepted_agreement) { toast.error("Please accept the agreement."); return; }
    if (!form.display_name || !form.email || !form.home_country) {
      toast.error("Display name, email, and country are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        cities_served: form.cities_text.split("\n").map((s) => s.trim()).filter(Boolean),
        certifications: form.certs_text.split(",").map((s) => s.trim()).filter(Boolean),
        years_in_business: parseInt(form.years_in_business, 10) || 0,
        insurance_liability_usd: parseInt(form.insurance_liability_usd, 10) || 0,
      };
      delete payload.cities_text;
      delete payload.certs_text;
      await api.post("/services/international/apply", payload);
      toast.success("Application received. We'll review within 5 business days.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application failed.");
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="intl-svc-apply">
        <button onClick={() => navigate("/services/international")}
          className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={12} /> Back to directory
        </button>
        <div className="text-[11px] tracking-[3px] text-[#8A6A00] font-bold uppercase mb-3">
          International Pro &mdash; Lead Marketplace Application
        </div>
        <h1 className="font-spectral text-4xl tracking-tight">Become a verified pro</h1>
        <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed max-w-2xl">
          List for free, get up to 1 free lead per month with a refundable
          $199 bond. Top up credits when you want more, or upgrade to Pro
          Sub for unlimited. We never touch your client&rsquo;s money.
        </p>

        <div className="mt-5 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4">
          <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">Pricing &mdash; only pay when you scale</div>
          <ul className="text-[12px] text-ink space-y-1 leading-relaxed">
            <li>&middot; <strong>Free listing</strong> &mdash; 1 lead / month with $199 refundable bond</li>
            <li>&middot; <strong>Pay-per-Lead</strong> &mdash; $29 per delivered lead</li>
            <li>&middot; <strong>Lead Pack 5 / 20</strong> &mdash; $119 / $399 (~$20 / lead)</li>
            <li>&middot; <strong>Pro Sub</strong> &mdash; $199 / mo or $1,990 / yr (unlimited)</li>
          </ul>
        </div>

        <div className="mt-8 bg-white border border-[#E5D08A] rounded-xl p-6 space-y-5">
          <Group title="Your service">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 col-span-full">
              {KINDS.map((k) => (
                <button key={k.v} type="button"
                  onClick={() => setForm((s) => ({ ...s, service_kind: k.v }))}
                  className={`text-[12px] h-9 rounded-full border ${
                    form.service_kind === k.v
                      ? "bg-ink border-ink text-white"
                      : "bg-white border-[#E5E5E5] text-ink"
                  }`}
                  data-testid={`intl-svc-kind-${k.v}`}>
                  {k.label}
                </button>
              ))}
            </div>
          </Group>

          <Group title="About you">
            <Input placeholder="Display name" value={form.display_name} onChange={set("display_name")} data-testid="intl-svc-apply-name" />
            <Input placeholder="Legal name" value={form.legal_name} onChange={set("legal_name")} />
            <Input placeholder="Email" type="email" value={form.email} onChange={set("email")} data-testid="intl-svc-apply-email" />
            <Input placeholder="Phone (with country code)" value={form.phone} onChange={set("phone")} />
            <Input placeholder="Home country" value={form.home_country} onChange={set("home_country")} data-testid="intl-svc-apply-country" />
            <Input placeholder="Website (optional)" value={form.website_url} onChange={set("website_url")} />
          </Group>

          <Group title="Cities served">
            <Textarea placeholder={"One city per line, e.g.\nParis\nLyon\nMarseille"} rows={4}
              value={form.cities_text} onChange={set("cities_text")} data-testid="intl-svc-apply-cities" />
          </Group>

          <Group title="Experience & credentials">
            <Input type="number" placeholder="Years in business" value={form.years_in_business} onChange={set("years_in_business")} min={0} />
            <Input placeholder="Certifications (comma-separated)" value={form.certs_text} onChange={set("certs_text")} />
          </Group>

          <Group title="Insurance">
            <Input placeholder="Carrier" value={form.insurance_carrier} onChange={set("insurance_carrier")} />
            <Input type="number" placeholder="Liability USD" value={form.insurance_liability_usd} onChange={set("insurance_liability_usd")} min={0} />
            <Input placeholder="Certificate URL" value={form.insurance_certificate_url} onChange={set("insurance_certificate_url")} />
          </Group>

          <Group title="Your story">
            <Textarea placeholder="Short bio &mdash; what makes you different?" rows={4} value={form.bio} onChange={set("bio")} />
            <Input placeholder="Profile photo URL" value={form.photo_url} onChange={set("photo_url")} />
          </Group>

          <div className="bg-[#FBF7EC] border border-[#E5D08A] rounded-lg p-4">
            <label className="flex items-start gap-2 text-[12px]">
              <Checkbox checked={form.accepted_agreement}
                onCheckedChange={(v) => setForm((s) => ({ ...s, accepted_agreement: !!v }))}
                data-testid="intl-svc-apply-ack" />
              <span>
                I confirm I am the service provider of record, hold all
                required local credentials and insurance, and understand
                Armstrong K9 Registry sells me LEADS only &mdash; never
                holds, escrows, or refunds money I receive from clients.
                Armstrong is not my employer or agent.
              </span>
            </label>
          </div>

          <Button
            onClick={submit}
            disabled={busy || !form.accepted_agreement}
            className="rounded-full bg-crimson hover:bg-ink text-white w-full h-11"
            data-testid="intl-svc-apply-submit"
          >
            <ShieldCheck size={16} weight="duotone" className="mr-2" />
            {busy ? "Submitting…" : "Submit application (free to apply)"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
